import React, { useState, useEffect, useRef, useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import languages from '../../common/data/languages';
import { detectBrowserLanguage, getBrowserInfo, getBrowserLanguages, mapToSupportedLanguage, supportedLngs } from '../../common/function/detectBrowserLanguage';

interface LanguageDropdownProps {
	variant?: 'inline' | 'floating';
	/** 是否顯示調試信息（僅開發時使用） */
	showDebug?: boolean;
}

/**
 * 獲取裝置類型
 */
const getDeviceType = (): { type: string; os: string; isMobile: boolean; isTablet: boolean } => {
	const ua = navigator.userAgent;
	const uaLower = ua.toLowerCase();

	let type = 'Desktop';
	let os = 'Unknown';
	let isMobile = false;
	let isTablet = false;

	// 檢測作業系統
	if (/windows/i.test(ua)) {
		os = 'Windows';
	} else if (/macintosh|mac os x/i.test(ua)) {
		os = 'macOS';
	} else if (/linux/i.test(ua) && !/android/i.test(ua)) {
		os = 'Linux';
	} else if (/android/i.test(ua)) {
		os = 'Android';
	} else if (/iphone|ipad|ipod/i.test(ua)) {
		os = 'iOS';
	}

	// 檢測裝置類型
	if (/ipad/i.test(ua) || (/android/i.test(ua) && !/mobile/i.test(ua))) {
		type = 'Tablet';
		isTablet = true;
	} else if (/mobile|iphone|ipod|android.*mobile|windows phone|blackberry|bb10|opera mini|opera mobi|iemobile/i.test(uaLower)) {
		type = 'Mobile';
		isMobile = true;
	}

	return { type, os, isMobile, isTablet };
};

/**
 * 調試面板組件
 */
const DebugPanel: React.FC<{ currentLang: string; onClose: () => void }> = ({ currentLang, onClose }) => {
	const browserInfo = useMemo(() => getBrowserInfo(), []);
	const deviceInfo = useMemo(() => getDeviceType(), []);
	const detectedLang = useMemo(() => detectBrowserLanguage(), []);
	const allBrowserLanguages = useMemo(() => getBrowserLanguages(), []);
	const mappedLang = useMemo(() => mapToSupportedLanguage(navigator.language), []);

	const panelStyle: React.CSSProperties = {
		position: 'fixed',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		width: '90%',
		maxWidth: 500,
		maxHeight: '80vh',
		overflowY: 'auto',
		background: '#1a1a2e',
		color: '#eaeaea',
		borderRadius: 12,
		boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
		zIndex: 99999,
		fontFamily: 'Monaco, Consolas, "Courier New", monospace',
		fontSize: 12,
	};

	const headerStyle: React.CSSProperties = {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		padding: '12px 16px',
		borderBottom: '1px solid #333',
		background: '#16213e',
		borderRadius: '12px 12px 0 0',
	};

	const sectionStyle: React.CSSProperties = {
		padding: '12px 16px',
		borderBottom: '1px solid #333',
	};

	const labelStyle: React.CSSProperties = {
		color: '#888',
		fontSize: 10,
		textTransform: 'uppercase',
		letterSpacing: 1,
		marginBottom: 4,
	};

	const valueStyle: React.CSSProperties = {
		color: '#4ecca3',
		wordBreak: 'break-all',
	};

	const codeStyle: React.CSSProperties = {
		background: '#0f0f1a',
		padding: '8px 12px',
		borderRadius: 6,
		marginTop: 4,
		fontSize: 11,
		overflowX: 'auto',
	};

	const badgeStyle = (color: string): React.CSSProperties => ({
		display: 'inline-block',
		padding: '2px 8px',
		borderRadius: 4,
		fontSize: 10,
		fontWeight: 600,
		background: color,
		color: '#fff',
		marginRight: 4,
	});

	return (
		<>
			{/* 背景遮罩 */}
			<div
				onClick={onClose}
				style={{
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					background: 'rgba(0,0,0,0.6)',
					zIndex: 99998,
				}}
			/>

			{/* 面板 */}
			<div style={panelStyle}>
				<div style={headerStyle}>
					<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
						<span style={{ fontSize: 16 }}>🔧</span>
						<span style={{ fontWeight: 600, fontSize: 14 }}>Language Debug Panel</span>
					</div>
					<button
						onClick={onClose}
						style={{
							background: 'transparent',
							border: 'none',
							color: '#888',
							fontSize: 20,
							cursor: 'pointer',
							padding: '0 4px',
						}}>
						×
					</button>
				</div>

				{/* 語言資訊 */}
				<div style={sectionStyle}>
					<div style={{ display: 'flex', gap: 20 }}>
						<div style={{ flex: 1 }}>
							<div style={labelStyle}>當前顯示語系</div>
							<div style={{ ...valueStyle, fontSize: 18, fontWeight: 600 }}>{currentLang}</div>
							<div style={{ color: '#888', fontSize: 11, marginTop: 2 }}>{languages.find((l) => l.code === currentLang)?.label || 'Unknown'}</div>
						</div>
						<div style={{ flex: 1 }}>
							<div style={labelStyle}>偵測結果語系</div>
							<div style={{ ...valueStyle, fontSize: 18, fontWeight: 600, color: '#f39c12' }}>{detectedLang}</div>
							<div style={{ color: '#888', fontSize: 11, marginTop: 2 }}>{languages.find((l) => l.code === detectedLang)?.label || 'Unknown'}</div>
						</div>
					</div>
				</div>

				{/* 裝置資訊 */}
				<div style={sectionStyle}>
					<div style={labelStyle}>裝置 & 瀏覽器</div>
					<div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
						<span style={badgeStyle(deviceInfo.isMobile ? '#e74c3c' : deviceInfo.isTablet ? '#9b59b6' : '#3498db')}>{deviceInfo.type}</span>
						<span style={badgeStyle('#2ecc71')}>{deviceInfo.os}</span>
						<span style={badgeStyle(browserInfo.isWebView ? '#e67e22' : '#1abc9c')}>{browserInfo.name}</span>
						{browserInfo.isWebView && <span style={badgeStyle('#e74c3c')}>WebView</span>}
					</div>
				</div>

				{/* Navigator 資訊 */}
				<div style={sectionStyle}>
					<div style={labelStyle}>navigator.language</div>
					<div style={codeStyle}>
						<span style={valueStyle}>{navigator.language || '(empty)'}</span>
						<span style={{ color: '#888' }}> → 映射為: </span>
						<span style={{ color: '#f39c12' }}>{mappedLang || '(null)'}</span>
					</div>
				</div>

				<div style={sectionStyle}>
					<div style={labelStyle}>navigator.languages</div>
					<div style={codeStyle}>
						{navigator.languages && navigator.languages.length > 0 ? (
							<div>
								[
								{navigator.languages.map((lang, idx) => (
									<span key={idx}>
										<span style={valueStyle}>"{lang}"</span>
										{idx < navigator.languages.length - 1 && <span style={{ color: '#888' }}>, </span>}
									</span>
								))}
								]
							</div>
						) : (
							<span style={{ color: '#888' }}>(empty or not supported)</span>
						)}
					</div>
				</div>

				{/* 所有偵測到的語言來源 */}
				<div style={sectionStyle}>
					<div style={labelStyle}>所有偵測到的語言來源</div>
					<div style={codeStyle}>
						{allBrowserLanguages.length > 0 ? (
							allBrowserLanguages.map((lang, idx) => (
								<div key={idx} style={{ marginBottom: 2 }}>
									<span style={{ color: '#888' }}>{idx + 1}. </span>
									<span style={valueStyle}>{lang}</span>
									<span style={{ color: '#888' }}> → </span>
									<span style={{ color: mapToSupportedLanguage(lang) ? '#4ecca3' : '#e74c3c' }}>{mapToSupportedLanguage(lang) || '(not supported)'}</span>
								</div>
							))
						) : (
							<span style={{ color: '#888' }}>(no languages detected)</span>
						)}
					</div>
				</div>

				{/* 支援的語言列表 */}
				<div style={sectionStyle}>
					<div style={labelStyle}>支援的語言列表</div>
					<div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
						{supportedLngs.map((code) => (
							<span
								key={code}
								style={{
									...badgeStyle(currentLang === code ? '#4ecca3' : '#555'),
									background: currentLang === code ? '#4ecca3' : '#333',
									color: currentLang === code ? '#1a1a2e' : '#888',
								}}>
								{code}
							</span>
						))}
					</div>
				</div>

				{/* User Agent */}
				<div style={{ ...sectionStyle, borderBottom: 'none' }}>
					<div style={labelStyle}>User Agent</div>
					<div style={{ ...codeStyle, fontSize: 10, color: '#888', lineHeight: 1.4 }}>{navigator.userAgent}</div>
				</div>
			</div>
		</>
	);
};

const LanguageDropdown: React.FC<LanguageDropdownProps> = ({ variant = 'inline', showDebug = false }) => {
	const { i18n } = useTranslation();
	const [open, setOpen] = useState(false);
	const [showTip, setShowTip] = useState(false);
	const [debugOpen, setDebugOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const lang = i18n.language || 'zh-TW';

	/**
	 * 初始化時偵測並設置語言（不使用 localStorage）
	 */
	useEffect(() => {
		// 使用自動偵測瀏覽器/系統語言
		const detectedLng = detectBrowserLanguage();

		if (showDebug) {
			const browserInfo = getBrowserInfo();
			console.log('[LanguageDropdown] Browser Info:', browserInfo);
			console.log('[LanguageDropdown] Detected Language:', detectedLng);
			console.log('[LanguageDropdown] Current i18n Language:', i18n.language);
		}

		// 如果偵測到的語言與當前語言不同，則切換
		if (detectedLng !== i18n.language) {
			i18n.changeLanguage(detectedLng);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Language attribute
	 */
	useLayoutEffect(() => {
		document.documentElement.setAttribute('lang', i18n.language);
	}, [i18n.language]);

	useEffect(() => {
		const onDocClick = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setOpen(false);
			}
		};
		if (open) document.addEventListener('mousedown', onDocClick);
		return () => document.removeEventListener('mousedown', onDocClick);
	}, [open]);

	// 匹配語言代碼，處理各種格式
	const findMatchingLanguage = (code: string) => {
		if (!code) return languages[0];

		// 完全匹配
		const exactMatch = languages.find((l) => l.code === code);
		if (exactMatch) return exactMatch;

		// 標準化後匹配
		const normalizedCode = code.toLowerCase().replace('_', '-');
		const normalizedMatch = languages.find((l) => l.code.toLowerCase() === normalizedCode);
		if (normalizedMatch) return normalizedMatch;

		// 主語言匹配（例如 zh-CN -> zh）
		const mainLang = code.split(/[-_]/)[0].toLowerCase();
		const mainLangMatch = languages.find((l) => l.code.toLowerCase().startsWith(mainLang));
		if (mainLangMatch) return mainLangMatch;

		return languages[0];
	};

	const currentLanguage = findMatchingLanguage(lang);

	const baseButtonStyle: React.CSSProperties = {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		border: '1px solid #dcdfe6',
		background: '#fff',
		color: '#303133',
		borderRadius: 16,
		padding: '4px 10px',
		fontSize: 12,
		cursor: 'pointer',
	};

	/**
	 * 處理語言切換
	 */
	const handleLanguageChange = (langCode: string) => {
		i18n.changeLanguage(langCode);
		setOpen(false);
		// 更新 document lang 屬性
		document.documentElement.setAttribute('lang', langCode);

		if (showDebug) {
			console.log('[LanguageDropdown] Language changed to:', langCode);
		}
	};

	/**
	 * 檢查語言是否被選中
	 */
	const isLanguageSelected = (langCode: string): boolean => {
		if (lang === langCode) return true;

		// 處理變體匹配
		const normalizedLang = lang.toLowerCase().replace('_', '-');
		const normalizedCode = langCode.toLowerCase().replace('_', '-');

		return normalizedLang === normalizedCode;
	};

	/**
	 * 調試按鈕樣式
	 */
	const debugButtonStyle: React.CSSProperties = {
		width: '100%',
		textAlign: 'center',
		padding: '8px 10px',
		background: '#f0f0f0',
		border: 'none',
		borderRadius: 6,
		cursor: 'pointer',
		color: '#666',
		fontSize: 11,
		marginTop: 4,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 4,
	};

	if (variant === 'floating') {
		return (
			<>
				<div
					ref={containerRef}
					style={{
						position: 'fixed',
						top: '50%',
						right: 0,
						transform: 'translateY(-50%)',
						zIndex: 9999,
					}}>
					{showTip && !open && (
						<div
							style={{
								position: 'absolute',
								top: '50%',
								right: 44,
								transform: 'translateY(-50%)',
								zIndex: 10000,
								background: 'rgba(0,0,0,0.8)',
								color: '#fff',
								padding: '4px 8px',
								borderRadius: 6,
								fontSize: 12,
								whiteSpace: 'nowrap',
								pointerEvents: 'none',
							}}>
							Language / 語言
						</div>
					)}

					<button
						onClick={() => setOpen((v) => !v)}
						onMouseEnter={() => setShowTip(true)}
						onMouseLeave={() => setShowTip(false)}
						title='Language / 語言'
						aria-label='toggle-language'
						style={{
							width: 36,
							height: 36,
							border: 'none',
							borderRadius: '6px 0 0 6px',
							background: '#2f3640',
							color: '#fff',
							cursor: 'pointer',
							boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							fontSize: 12,
							lineHeight: 1,
							padding: 0,
						}}>
						{currentLanguage.shortLabel}
					</button>

					{open && (
						<div
							style={{
								position: 'absolute',
								top: '50%',
								right: 44,
								transform: 'translateY(-50%)',
								minWidth: 160,
								background: '#fff',
								border: '1px solid #ebeef5',
								borderRadius: 8,
								boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
								padding: 6,
							}}>
							{languages.map((lng) => (
								<button
									key={lng.code}
									type='button'
									onClick={() => handleLanguageChange(lng.code)}
									style={{
										width: '100%',
										textAlign: 'left',
										padding: '6px 10px',
										background: 'transparent',
										border: 'none',
										borderRadius: 6,
										cursor: 'pointer',
										color: isLanguageSelected(lng.code) ? '#409EFF' : '#303133',
										fontWeight: isLanguageSelected(lng.code) ? 600 : 400,
									}}>
									{lng.label}
								</button>
							))}

							{/* Debug 按鈕 */}
							{showDebug && (
								<button type='button' onClick={() => setDebugOpen(true)} style={debugButtonStyle}>
									<span>🔧</span>
									<span>Debug Panel</span>
								</button>
							)}
						</div>
					)}
				</div>

				{/* Debug Panel */}
				{debugOpen && <DebugPanel currentLang={lang} onClose={() => setDebugOpen(false)} />}
			</>
		);
	}

	return (
		<>
			<div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
				<button type='button' onClick={() => setOpen((v) => !v)} title='Language / 語言' style={baseButtonStyle}>
					<span role='img' aria-label='language'>
						🌐
					</span>
					<span>Language / 語言</span>
					<span style={{ fontSize: 10, color: '#909399' }}>{open ? '▲' : '▼'}</span>
				</button>

				{open && (
					<div
						style={{
							position: 'absolute',
							right: 0,
							marginTop: 6,
							minWidth: 160,
							background: '#fff',
							border: '1px solid #ebeef5',
							borderRadius: 8,
							boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
							padding: 6,
							zIndex: 10,
						}}>
						{languages.map((lng) => (
							<button
								key={lng.code}
								type='button'
								onClick={() => handleLanguageChange(lng.code)}
								style={{
									width: '100%',
									textAlign: 'left',
									padding: '6px 10px',
									background: 'transparent',
									border: 'none',
									borderRadius: 6,
									cursor: 'pointer',
									color: isLanguageSelected(lng.code) ? '#409EFF' : '#303133',
									fontWeight: isLanguageSelected(lng.code) ? 600 : 400,
								}}>
								{lng.label}
							</button>
						))}

						{/* Debug 按鈕 */}
						{showDebug && (
							<button type='button' onClick={() => setDebugOpen(true)} style={debugButtonStyle}>
								<span>🔧</span>
								<span>Debug Panel</span>
							</button>
						)}
					</div>
				)}
			</div>

			{/* Debug Panel */}
			{debugOpen && <DebugPanel currentLang={lang} onClose={() => setDebugOpen(false)} />}
		</>
	);
};

export default LanguageDropdown;
